import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  getNotificationTriggerConfigs,
  updateNotificationTriggerConfig,
  getNotificationCampaigns,
  createNotificationCampaign,
  updateNotificationCampaign,
  deleteNotificationCampaign,
  sendNotificationCampaign,
  getNotificationCampaignStats,
  getSurveyStats,
  getSurveyResponses,
  getHubs,
  getExpiryReminderConfigs,
  updateExpiryReminderConfig,
  getExpiryReminderLogs,
  getFunnelConfigs,
  updateFunnelConfig,
  getFunnelLogs,
  getAudiencesList,
} from '../config/api';
import useIsMobile from '../hooks/useIsMobile';

// ─── Shared mini-components (same style language as WhatsAppPortal) ───────────

const Badge = ({ children, variant = 'default' }) => {
  const styles = {
    success: { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
    warning: { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
    danger:  { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
    info:    { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
    purple:  { bg: '#f3e8ff', color: '#6b21a8', border: '#d8b4fe' },
    default: { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' },
  };
  const s = styles[variant] || styles.default;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '2px 10px',
      borderRadius: 20, fontSize: 11, fontWeight: 600, letterSpacing: 0.3,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>{children}</span>
  );
};

const statusVariant = (status) => ({
  draft: 'default', scheduled: 'warning', sending: 'info',
  sent: 'success', cancelled: 'danger',
}[status] || 'default');

const fmtDate = (d) => d
  ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

const Card = ({ children, style = {} }) => (
  <div style={{
    background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
    padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', ...style,
  }}>{children}</div>
);

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 12 }}>{children}</div>
);

const Input = ({ label, ...props }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>{label}</label>}
    <input className="form-control" style={{ width: '100%', boxSizing: 'border-box' }} {...props} />
  </div>
);

const Textarea = ({ label, ...props }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>{label}</label>}
    <textarea className="form-control" style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }} {...props} />
  </div>
);

const Select = ({ label, children, ...props }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>{label}</label>}
    <select className="form-control" style={{ width: '100%' }} {...props}>{children}</select>
  </div>
);

const Btn = ({ variant = 'primary', disabled, children, style = {}, ...props }) => {
  const colors = {
    primary: { bg: '#1e40af', hover: '#1d4ed8' },
    success: { bg: '#16a34a', hover: '#15803d' },
    danger:  { bg: '#dc2626', hover: '#b91c1c' },
    ghost:   { bg: 'white', hover: '#f3f4f6', color: '#374151', border: '#d1d5db' },
  };
  const c = colors[variant] || colors.primary;
  return (
    <button
      disabled={disabled}
      style={{
        padding: '8px 18px', border: c.border ? `1px solid ${c.border}` : 'none',
        borderRadius: 7, cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: 600, fontSize: 13, color: c.color || 'white',
        background: disabled ? '#9ca3af' : c.bg,
        transition: 'background 0.15s', ...style,
      }}
      {...props}
    >{children}</button>
  );
};

const Toggle = ({ checked, onChange, label }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 40, height: 22, borderRadius: 11, padding: 2,
        background: checked ? '#1e40af' : '#d1d5db',
        transition: 'background 0.2s', position: 'relative', cursor: 'pointer',
      }}
    >
      <div style={{
        width: 18, height: 18, borderRadius: '50%', background: 'white',
        position: 'absolute', top: 2, left: checked ? 20 : 2,
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
    {label && <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>}
  </label>
);

const Modal = ({ open, onClose, title, children, width = 560 }) => {
  const isMobile = useIsMobile();
  if (!open) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 12 : 20,
    }} onClick={onClose}>
      <div
        style={{
          background: 'white', borderRadius: 12, padding: isMobile ? 16 : 28, width: '100%', maxWidth: width,
          maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: isMobile ? 26 : 20, cursor: 'pointer', color: '#6b7280', lineHeight: 1, ...(isMobile ? { minWidth: 44, minHeight: 44 } : {}) }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
};

const CharCount = ({ value, max, style = {} }) => (
  <div style={{ fontSize: 11, color: value.length > max ? '#dc2626' : '#9ca3af', textAlign: 'right', marginTop: 3, ...style }}>
    {value.length} / {max}
  </div>
);

// ─── TRIGGER CONFIG TAB ───────────────────────────────────────────────────────

const SUBTYPE_LABELS = {
  cart_abandon:       'Cart Abandonment',
  // category_browsed:   'Category Browsed (No Cart Add)',
  wishlist_inactivity:'Wishlist Inactivity',
};

const DEEPLINK_EXAMPLES = [
  'solar.zunhome.in/app/openRoute?pageName=cartscreen', 'solar.zunhome.in/app/openRoute?pageName=wishlist',
];

const TriggerConfigTab = () => {
  const isMobile = useIsMobile();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null); // config object being edited
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNotificationTriggerConfigs();
      setConfigs(res.data || []);
    } catch (e) {
      toast.error('Failed to load trigger configs: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (cfg) => {
    setForm({
      is_enabled: cfg.is_enabled,
      delay_minutes: cfg.delay_minutes,
      push: cfg.channel_config?.push !== false,
      in_app: cfg.channel_config?.in_app !== false,
      headline: cfg.template?.headline || '',
      body: cfg.template?.body || '',
      image_url: cfg.template?.image_url || '',
      deeplink_url: cfg.template?.deeplink_url || '',
    });
    setEditModal(cfg);
  };

  const handleSave = async () => {
    if (!form.headline.trim()) { toast.error('Headline is required'); return; }
    if (form.headline.length > 50) { toast.error('Headline max 50 chars'); return; }
    if (!form.body.trim()) { toast.error('Body is required'); return; }
    if (form.body.length > 120) { toast.error('Body max 120 chars'); return; }
    setSaving(true);
    try {
      await updateNotificationTriggerConfig(editModal.subtype, {
        is_enabled: form.is_enabled,
        delay_minutes: parseInt(form.delay_minutes) || 60,
        channel_config: { push: form.push, in_app: form.in_app },
        template: {
          headline: form.headline,
          body: form.body,
          image_url: form.image_url || null,
          deeplink_url: form.deeplink_url || null,
        },
      });
      toast.success('Trigger config saved');
      setEditModal(null);
      load();
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>⏳ Loading trigger configs…</div>;

  return (
    <div>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 18 }}>
        Configure each transactional trigger — enable/disable, edit message template, adjust delay, and choose channels.
        Changes take effect on the next cron cycle (every 30 min).
      </div>

      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, ...(isMobile ? { minWidth: 680 } : {}) }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              {['Trigger', 'Status', 'Delay', 'Channels', 'Headline', 'Actions'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {configs.map(cfg => (
              <tr key={cfg.subtype} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '12px 16px', fontWeight: 600, color: '#111827' }}>
                  {SUBTYPE_LABELS[cfg.subtype] || cfg.subtype}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <Badge variant={cfg.is_enabled ? 'success' : 'default'}>
                    {cfg.is_enabled ? '● Enabled' : '○ Disabled'}
                  </Badge>
                </td>
                <td style={{ padding: '12px 16px', color: '#374151' }}>
                  {cfg.delay_minutes >= 1440
                    ? `${(cfg.delay_minutes / 1440).toFixed(0)}d`
                    : cfg.delay_minutes >= 60
                      ? `${(cfg.delay_minutes / 60).toFixed(0)}h`
                      : `${cfg.delay_minutes}m`}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {cfg.channel_config?.push !== false && <Badge variant="info">Push</Badge>}
                    {cfg.channel_config?.in_app !== false && <Badge variant="purple">In-App</Badge>}
                  </div>
                </td>
                <td style={{ padding: '12px 16px', color: '#374151', maxWidth: 240 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cfg.template?.headline || '—'}
                  </div>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <Btn variant="ghost" style={{ padding: '5px 14px', fontSize: 12 }} onClick={() => openEdit(cfg)}>
                    ✏️ Edit
                  </Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      <Modal open={!!editModal} onClose={() => setEditModal(null)} title={`Edit — ${SUBTYPE_LABELS[editModal?.subtype] || editModal?.subtype}`} width={620}>
        {editModal && (
          <div>
            <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
              <Toggle
                checked={form.is_enabled}
                onChange={v => setForm(f => ({ ...f, is_enabled: v }))}
                label="Enabled"
              />
              <div style={{ display: 'flex', gap: 12 }}>
                <Toggle checked={form.push} onChange={v => setForm(f => ({ ...f, push: v }))} label="Push" />
                <Toggle checked={form.in_app} onChange={v => setForm(f => ({ ...f, in_app: v }))} label="In-App" />
              </div>
            </div>

            <Input
              label="Delay (minutes)"
              type="number"
              min={1}
              value={form.delay_minutes}
              onChange={e => setForm(f => ({ ...f, delay_minutes: e.target.value }))}
            />

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>
                Headline <span style={{ color: '#9ca3af' }}>— max 50 chars, supports {'{{variable}}'}</span>
              </label>
              <input className="form-control" value={form.headline}
                onChange={e => setForm(f => ({ ...f, headline: e.target.value }))}
                maxLength={50} style={{ width: '100%', boxSizing: 'border-box' }} />
              <CharCount value={form.headline} max={50} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>
                Body <span style={{ color: '#9ca3af' }}>— max 120 chars, supports {'{{variable}}'}</span>
              </label>
              <textarea className="form-control" rows={3} value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                maxLength={120} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }} />
              <CharCount value={form.body} max={120} />
            </div>

            <Input
              label="Image URL (optional)"
              type="url"
              placeholder="https://..."
              value={form.image_url}
              onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
            />

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>
                Deeplink URL
              </label>
              <input className="form-control" value={form.deeplink_url}
                onChange={e => setForm(f => ({ ...f, deeplink_url: e.target.value }))}
                style={{ width: '100%', boxSizing: 'border-box' }} />
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {DEEPLINK_EXAMPLES.map(d => (
                  <button key={d} onClick={() => setForm(f => ({ ...f, deeplink_url: d }))}
                    style={{ fontSize: 11, padding: '2px 8px', border: '1px solid #d1d5db', borderRadius: 4, background: '#f9fafb', cursor: 'pointer', color: '#374151' }}>
                    {d}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variant="primary" disabled={saving} onClick={handleSave}>
                {saving ? '⏳ Saving…' : '💾 Save Changes'}
              </Btn>
              <Btn variant="ghost" onClick={() => setEditModal(null)}>Cancel</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ─── CAMPAIGN FORM (shared by Promotional + Survey) ───────────────────────────

const EMPTY_QUESTION = { question: '', response_type: 'single_select', options: ['', ''] };

const EMPTY_CAMPAIGN = {
  type: 'promotional',
  headline: '',
  body: '',
  image_url: '',
  deeplink_url: '',
  audience_filter: { min_order_count: '', last_active_days: '', warehouse_ids: [], first_order_date_from: '', first_order_date_to: '', user_ids: [] },
  channel_config: { push: true, in_app: true },
  scheduled_at: '',
  survey_config: {
    questions: [{ ...EMPTY_QUESTION }],
  },
};

const CampaignForm = ({ initial = {}, onSave, onCancel, saving }) => {
  const [f, setF] = useState({
    ...EMPTY_CAMPAIGN,
    ...initial,
    audience_filter: {
      min_order_count: '',
      last_active_days: '',
      warehouse_ids: [],
      first_order_date_from: '',
      first_order_date_to: '',
      user_ids: [],
      ...(initial.audience_filter || {}),
    },
    survey_config: initial.survey_config || { questions: [{ ...EMPTY_QUESTION }] },
  });
  const isSurvey = f.type === 'survey';
  const [hubs, setHubs] = useState([]);
  const [audienceOptions, setAudienceOptions] = useState([]);
  const [loadingAudiences, setLoadingAudiences] = useState(true);
  const [csvFileName, setCsvFileName] = useState(
    (initial.audience_filter?.user_ids?.length)
      ? `${initial.audience_filter.user_ids.length} user IDs loaded`
      : ''
  );

  // Parse a CSV file and extract numeric user IDs from it.
  // Accepts single-column files or any column named "userId", "user_id", or "id".
  const handleCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result || '';
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (!lines.length) { toast.error('CSV is empty'); return; }

      // Detect header row
      const firstLine = lines[0].toLowerCase();
      const headerCols = firstLine.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
      const knownHeaders = ['userid', 'user_id', 'id'];
      const colIdx = headerCols.findIndex(c => knownHeaders.includes(c));
      const hasHeader = colIdx !== -1 || isNaN(Number(lines[0].split(',')[0]));
      const dataLines = hasHeader ? lines.slice(1) : lines;
      const targetCol = colIdx !== -1 ? colIdx : 0;

      const ids = [];
      const invalid = [];
      dataLines.forEach((line, i) => {
        const cells = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
        const raw = cells[targetCol] || cells[0];
        const num = Number(raw);
        if (raw && Number.isInteger(num) && num > 0) {
          ids.push(num);
        } else if (raw) {
          invalid.push(i + (hasHeader ? 2 : 1));
        }
      });

      if (!ids.length) { toast.error('No valid user IDs found in CSV'); return; }
      if (invalid.length) toast.warn(`${invalid.length} row(s) skipped — not valid integers`);

      const unique = [...new Set(ids)];
      setAud('user_ids', unique);
      setCsvFileName(`${file.name} — ${unique.length} user IDs`);
      toast.success(`Loaded ${unique.length} user IDs from CSV`);
    };
    reader.readAsText(file);
    // Reset input so re-uploading the same file still triggers onChange
    e.target.value = '';
  };

  useEffect(() => {
    getHubs()
      .then(res => setHubs(res.data || []))
      .catch(() => {});
    getAudiencesList({ status: 'active' })
      .then(res => {
        const audiences = res?.data?.audiences || res?.data || [];
        setAudienceOptions(audiences.map(a => ({ id: a.id, name: a.name, member_count: a.member_count })));
      })
      .catch(() => {})
      .finally(() => setLoadingAudiences(false));
  }, []);

  const set = (key, val) => setF(prev => ({ ...prev, [key]: val }));
  const setAud = (key, val) => setF(prev => ({ ...prev, audience_filter: { ...prev.audience_filter, [key]: val } }));
  const setChan = (key, val) => setF(prev => ({ ...prev, channel_config: { ...prev.channel_config, [key]: val } }));

  // ── Multi-question helpers ──────────────────────────────────────────────
  const getQuestions = () => f.survey_config?.questions || [];

  const setQuestion = (qi, key, val) => {
    const updated = getQuestions().map((q, i) => i === qi ? { ...q, [key]: val } : q);
    setF(prev => ({ ...prev, survey_config: { ...prev.survey_config, questions: updated } }));
  };

  const addQuestion = () => {
    setF(prev => ({
      ...prev,
      survey_config: {
        ...prev.survey_config,
        questions: [...getQuestions(), { ...EMPTY_QUESTION }],
      },
    }));
  };

  const removeQuestion = (qi) => {
    setF(prev => ({
      ...prev,
      survey_config: {
        ...prev.survey_config,
        questions: getQuestions().filter((_, i) => i !== qi),
      },
    }));
  };

  const addOption = (qi) => setQuestion(qi, 'options', [...(getQuestions()[qi].options || []), '']);
  const removeOption = (qi, oi) => setQuestion(qi, 'options', getQuestions()[qi].options.filter((_, i) => i !== oi));
  const setOption = (qi, oi, val) => setQuestion(qi, 'options', getQuestions()[qi].options.map((o, i) => i === oi ? val : o));

  const toggleHub = (hubId) => {
    const current = f.audience_filter.warehouse_ids || [];
    const updated = current.includes(hubId)
      ? current.filter(id => id !== hubId)
      : [...current, hubId];
    setAud('warehouse_ids', updated);
  };

  const handleSubmit = (intent) => {
    // intent: 'draft' | 'schedule' | 'send_now'
    if (!f.headline.trim()) { toast.error('Headline is required'); return; }
    if (f.headline.length > 50) { toast.error('Headline max 50 chars'); return; }
    if (!f.body.trim()) { toast.error('Body is required'); return; }
    if (f.body.length > 120) { toast.error('Body max 120 chars'); return; }
    if (isSurvey) {
      const questions = getQuestions();
      if (!questions.length) { toast.error('Add at least one question'); return; }
      for (let i = 0; i < questions.length; i++) {
        if (!questions[i].question.trim()) { toast.error(`Question ${i + 1} text is required`); return; }
        if (questions[i].response_type === 'single_select') {
          const validOpts = questions[i].options.filter(o => o.trim());
          if (validOpts.length < 2) { toast.error(`Question ${i + 1} needs at least 2 options`); return; }
        }
      }
    }
    if (intent === 'schedule' && !f.scheduled_at) { toast.error('Pick a date/time to schedule'); return; }

    const payload = {
      type: f.type,
      headline: f.headline,
      body: f.body,
      image_url: f.image_url || null,
      deeplink_url: f.deeplink_url || null,
      audience_filter: {
        ...(f.audience_filter.audience_id           ? { audience_id:           f.audience_filter.audience_id           } : {}),
        ...(f.audience_filter.min_order_count       ? { min_order_count:       parseInt(f.audience_filter.min_order_count) } : {}),
        ...(f.audience_filter.last_active_days      ? { last_active_days:      parseInt(f.audience_filter.last_active_days) } : {}),
        ...(f.audience_filter.warehouse_ids?.length ? { warehouse_ids:         f.audience_filter.warehouse_ids } : {}),
        ...(f.audience_filter.first_order_date_from ? { first_order_date_from: f.audience_filter.first_order_date_from } : {}),
        ...(f.audience_filter.first_order_date_to   ? { first_order_date_to:   f.audience_filter.first_order_date_to   } : {}),
        ...(f.audience_filter.user_ids?.length       ? { user_ids:             f.audience_filter.user_ids }                         : {}),
      },
      channel_config: f.channel_config,
      // Only include scheduled_at when explicitly scheduling
      ...(intent === 'schedule' && f.scheduled_at
        ? { scheduled_at: new Date(f.scheduled_at).toISOString() }
        : {}),
      // status hint — backend resolves the real status
      status: intent === 'draft' ? 'draft' : undefined,
      ...(isSurvey ? {
        survey_config: {
          questions: getQuestions().map(q => ({
            question: q.question,
            response_type: q.response_type,
            options: q.response_type === 'single_select'
              ? q.options.filter(o => o.trim())
              : undefined,
          })),
        },
      } : {}),
    };
    onSave(payload, intent);
  };

  return (
    <div>
      {/* Type selector — only show on new campaigns */}
      {/* {!initial.id && (
        <div style={{ display: 'flex', gap: 0, marginBottom: 20, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', width: 'fit-content' }}>
          {[['promotional', '📢', 'Promotional'], ['survey', '📊', 'Survey']].map(([val, icon, label]) => (
            <button key={val} onClick={() => set('type', val)} style={{
              padding: '8px 20px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
              background: f.type === val ? '#1e40af' : 'white',
              color: f.type === val ? 'white' : '#374151',
            }}>{icon} {label}</button>
          ))}
        </div>
      )} */}

      {/* Compose */}
      <Card style={{ marginBottom: 16 }}>
        <SectionTitle>📝 Compose</SectionTitle>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>Headline <span style={{ color: '#9ca3af' }}>max 50 chars</span></label>
          <input className="form-control" value={f.headline} onChange={e => set('headline', e.target.value)} maxLength={50} style={{ width: '100%', boxSizing: 'border-box' }} />
          <CharCount value={f.headline} max={50} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>Body <span style={{ color: '#9ca3af' }}>max 120 chars</span></label>
          <textarea className="form-control" rows={3} value={f.body} onChange={e => set('body', e.target.value)} maxLength={120} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }} />
          <CharCount value={f.body} max={120} />
        </div>
        <Input label="Image URL (optional)" type="url" placeholder="https://..." value={f.image_url} onChange={e => set('image_url', e.target.value)} />
        {!isSurvey && (
          <Input label="Deeplink URL (optional)" placeholder="solar.zunhome.in/app/openRoute?pageName=" value={f.deeplink_url} onChange={e => set('deeplink_url', e.target.value)} />
        )}
      </Card>

      {/* Survey config — multi-question */}
      {isSurvey && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <SectionTitle style={{ marginBottom: 0 }}>📊 Survey Questions</SectionTitle>
            <button
              onClick={addQuestion}
              style={{ fontSize: 12, color: '#1e40af', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontWeight: 600 }}
            >
              + Add Question
            </button>
          </div>

          {getQuestions().map((q, qi) => (
            <div key={qi} style={{
              border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12,
              background: '#fafafa', position: 'relative',
            }}>
              {/* Question number + remove */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Question {qi + 1}</span>
                {getQuestions().length > 1 && (
                  <button
                    onClick={() => removeQuestion(qi)}
                    style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '2px 10px', cursor: 'pointer', color: '#991b1b', fontSize: 12, fontWeight: 700 }}
                  >
                    Remove
                  </button>
                )}
              </div>

              {/* Question text */}
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 }}>Question Text</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={q.question}
                  onChange={e => setQuestion(qi, 'question', e.target.value)}
                  placeholder="e.g. How satisfied are you with your delivery?"
                  style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>

              {/* Response type */}
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 }}>Response Type</label>
                <select
                  className="form-control"
                  value={q.response_type}
                  onChange={e => setQuestion(qi, 'response_type', e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="single_select">Single Select (chips)</option>
                  <option value="star">Star Rating (1–5)</option>
                  <option value="thumbs">Thumbs Up / Down</option>
                  <option value="text">Open Text</option>
                </select>
              </div>

              {/* Options — only for single_select */}
              {q.response_type === 'single_select' && (
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Options (2–5)</label>
                  {(q.options || []).map((opt, oi) => (
                    <div key={oi} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input
                        className="form-control"
                        value={opt}
                        onChange={e => setOption(qi, oi, e.target.value)}
                        placeholder={`Option ${oi + 1}`}
                        style={{ flex: 1 }}
                      />
                      {q.options.length > 2 && (
                        <button
                          onClick={() => removeOption(qi, oi)}
                          style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '0 10px', cursor: 'pointer', color: '#991b1b', fontWeight: 700 }}
                        >×</button>
                      )}
                    </div>
                  ))}
                  {q.options.length < 5 && (
                    <button
                      onClick={() => addOption(qi)}
                      style={{ fontSize: 12, color: '#1e40af', background: 'none', border: '1px dashed #93c5fd', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}
                    >
                      + Add option
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      {/* Audience */}
      <Card style={{ marginBottom: 16 }}>
        <SectionTitle>🎯 Audience <span style={{ fontWeight: 400, color: '#9ca3af' }}>— leave blank for all users</span></SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 14 }}>
          <Input label="Min Order Count" type="number" min={0} placeholder="e.g. 1" value={f.audience_filter.min_order_count} onChange={e => setAud('min_order_count', e.target.value)} />
          <Input label="Active in last N days" type="number" min={1} placeholder="e.g. 30" value={f.audience_filter.last_active_days} onChange={e => setAud('last_active_days', e.target.value)} />
        </div>

        {/* First order date range filter */}
        {/* Reusable Audience Module Selector */}
        <div style={{ marginBottom: 20, padding: 14, background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>
            Target Reusable Audience
          </label>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <select
              className="form-control"
              value={f.audience_filter.audience_id || ''}
              onChange={e => setAud('audience_id', e.target.value || '')}
              style={{ maxWidth: 400 }}
            >
              <option value="">{loadingAudiences ? 'Loading audiences...' : '— No audience (target by filters below) —'}</option>
              {audienceOptions.map(a => (
                <option key={a.id} value={a.id}>
                  AUD-{a.id} — {a.name} ({a.member_count?.toLocaleString() || 0} members)
                </option>
              ))}
            </select>
            {f.audience_filter.audience_id && (
              <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
                ✓ Targeted to Audience AUD-{f.audience_filter.audience_id}
              </span>
            )}
          </div>
          <div style={{ fontSize: '11px', color: '#3b82f6', marginTop: 4 }}>
            Selecting an Audience overrides inline filters below and targets members from that saved segment.
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
            First Order Date Range <span style={{ color: '#9ca3af', fontWeight: 400 }}>— target users whose very first order falls within this window</span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, color: '#6b7280' }}>From</span>
              <input
                type="date"
                className="form-control"
                style={{ width: 170 }}
                value={f.audience_filter.first_order_date_from}
                max={f.audience_filter.first_order_date_to || undefined}
                onChange={e => setAud('first_order_date_from', e.target.value)}
              />
            </div>
            <span style={{ fontSize: 13, color: '#9ca3af', marginTop: 16 }}>→</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, color: '#6b7280' }}>To</span>
              <input
                type="date"
                className="form-control"
                style={{ width: 170 }}
                value={f.audience_filter.first_order_date_to}
                min={f.audience_filter.first_order_date_from || undefined}
                onChange={e => setAud('first_order_date_to', e.target.value)}
              />
            </div>
            {(f.audience_filter.first_order_date_from || f.audience_filter.first_order_date_to) && (
              <button
                onClick={() => { setAud('first_order_date_from', ''); setAud('first_order_date_to', ''); }}
                style={{ marginTop: 16, fontSize: 11, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* User ID CSV upload */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
            Target specific users via CSV <span style={{ color: '#9ca3af', fontWeight: 400 }}>— upload a .csv with a column named <code>userId</code>, <code>user_id</code>, or <code>id</code> (or just a single column of IDs)</span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '7px 16px', borderRadius: 7, cursor: 'pointer',
              border: '1px solid #d1d5db', background: '#f9fafb',
              fontSize: 13, fontWeight: 500, color: '#374151',
            }}>
              📂 Choose CSV
              <input type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleCsvUpload} />
            </label>
            {csvFileName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#059669', fontWeight: 500 }}>✓ {csvFileName}</span>
                <button
                  onClick={() => { setAud('user_ids', []); setCsvFileName(''); }}
                  style={{ fontSize: 11, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Clear
                </button>
              </div>
            ) : (
              <span style={{ fontSize: 12, color: '#9ca3af' }}>No file chosen — all users matching other filters will be targeted</span>
            )}
          </div>
          {(f.audience_filter.user_ids?.length > 0) && (
            <div style={{ marginTop: 8, padding: '6px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 12, color: '#1e40af' }}>
              🎯 Campaign will be sent to exactly <strong>{f.audience_filter.user_ids.length}</strong> user ID{f.audience_filter.user_ids.length !== 1 ? 's' : ''} from the CSV
              {(f.audience_filter.min_order_count || f.audience_filter.last_active_days || f.audience_filter.first_order_date_from || f.audience_filter.first_order_date_to || f.audience_filter.warehouse_ids?.length)
                ? ', intersected with the other active filters above.'
                : '.'}
            </div>
          )}
        </div>

        {/* Hub filter */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 8 }}>
            Hubs <span style={{ color: '#9ca3af', fontWeight: 400 }}>— send only to users who ordered from these hubs (leave empty for all)</span>
          </label>
          {hubs.length === 0 ? (
            <div style={{ fontSize: 12, color: '#9ca3af' }}>Loading hubs…</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {hubs.map(hub => {
                const selected = (f.audience_filter.warehouse_ids || []).includes(hub.id);
                return (
                  <label key={hub.id} style={{
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                    cursor: 'pointer', padding: '5px 12px', borderRadius: 20,
                    border: `1px solid ${selected ? '#1e40af' : '#d1d5db'}`,
                    background: selected ? '#eff6ff' : 'white',
                    color: selected ? '#1e40af' : '#374151',
                    transition: 'all 0.15s', userSelect: 'none',
                  }}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleHub(hub.id)}
                      style={{ display: 'none' }}
                    />
                    {selected ? '✓ ' : ''}{hub.name}
                    {hub.city ? <span style={{ fontSize: 11, opacity: 0.6 }}>({hub.city})</span> : null}
                  </label>
                );
              })}
            </div>
          )}
          {(f.audience_filter.warehouse_ids || []).length > 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#1e40af' }}>
              {f.audience_filter.warehouse_ids.length} hub{f.audience_filter.warehouse_ids.length > 1 ? 's' : ''} selected
              <button onClick={() => setAud('warehouse_ids', [])}
                style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 11, textDecoration: 'underline' }}>
                Clear
              </button>
            </div>
          )}
        </div>
      </Card>

      {/* Channel + Schedule */}
      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>📡 Channels & Schedule</SectionTitle>
        <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
          <Toggle checked={f.channel_config.push} onChange={v => setChan('push', v)} label="Push Notification" />
          <Toggle checked={f.channel_config.in_app} onChange={v => setChan('in_app', v)} label="In-App Centre" />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>
            Schedule for later <span style={{ color: '#9ca3af' }}>— leave blank to send immediately or save as draft</span>
          </label>
          <input type="datetime-local" className="form-control" value={f.scheduled_at}
            onChange={e => set('scheduled_at', e.target.value)} style={{ width: 'fit-content' }} />
        </div>
      </Card>

      {/* Action buttons — clear intent */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Send Now — no schedule needed */}
        <Btn variant="success" disabled={saving} onClick={() => handleSubmit('send_now')}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {saving ? '⏳ Sending…' : '▶ Send Now'}
        </Btn>

        {/* Schedule — requires scheduled_at to be filled */}
        <Btn variant="primary" disabled={saving || !f.scheduled_at} onClick={() => handleSubmit('schedule')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: f.scheduled_at ? 1 : 0.5 }}>
          {saving ? '⏳ Scheduling…' : '🕐 Schedule'}
        </Btn>

        {/* Save as draft */}
        <Btn variant="ghost" disabled={saving} onClick={() => handleSubmit('draft')}>
          💾 Save as Draft
        </Btn>

        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>

      {/* Helper text */}
      <div style={{ marginTop: 10, fontSize: 11, color: '#9ca3af' }}>
        <strong>Send Now</strong> — dispatches immediately to all matching users.&nbsp;&nbsp;
        <strong>Schedule</strong> — pick a date/time above; the system sends it automatically at that time.&nbsp;&nbsp;
        <strong>Draft</strong> — saves without sending; you can send later from the campaign list.
      </div>
    </div>
  );
};

// ─── STATS MODAL ──────────────────────────────────────────────────────────────

const StatsModal = ({ open, onClose, campaign }) => {
  const [stats, setStats] = useState(null);
  const [surveyStats, setSurveyStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !campaign) return;
    setLoading(true);
    const promises = [getNotificationCampaignStats(campaign.id)];
    if (campaign.type === 'survey') promises.push(getSurveyStats(campaign.id));
    Promise.all(promises)
      .then(([s, sv]) => { setStats(s.data); if (sv) setSurveyStats(sv.data); })
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [open, campaign]);

  if (!campaign) return null;

  const statBox = (label, value, color = '#1e40af') => (
    <div style={{ flex: 1, minWidth: 100, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value ?? '—'}</div>
      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>{label}</div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={`Stats — ${campaign.headline}`} width={640}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>⏳ Loading stats…</div>
      ) : stats ? (
        <div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
            {statBox('Targeted', stats.campaign?.total_targeted)}
            {statBox('Push Sent', stats.campaign?.total_push_sent, '#1e40af')}
            {statBox('In-App', stats.campaign?.total_in_app_created, '#7c3aed')}
            {statBox('Read', stats.readCount, '#059669')}
          </div>

          {campaign.type === 'survey' && surveyStats && (
            <div>
              <SectionTitle>📊 Survey Responses</SectionTitle>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                {statBox('Total Responses', surveyStats.totalResponses, '#059669')}
                {statBox('Skipped', surveyStats.totalSkipped, '#d97706')}
              </div>
              {surveyStats.responses && surveyStats.responses.length > 0 && (
                <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Response</th>
                        <th style={{ padding: '8px 14px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Count</th>
                        <th style={{ padding: '8px 14px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {surveyStats.responses.map((r, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '8px 14px', fontWeight: 500 }}>{r.response_value}</td>
                          <td style={{ padding: '8px 14px', textAlign: 'right' }}>{r.count}</td>
                          <td style={{ padding: '8px 14px', textAlign: 'right', color: '#6b7280' }}>
                            {surveyStats.totalResponses > 0 ? ((r.count / surveyStats.totalResponses) * 100).toFixed(0) : 0}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
};

// ─── CAMPAIGNS TAB ────────────────────────────────────────────────────────────

const CampaignsTab = ({ type }) => {
  const isMobile = useIsMobile();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'create' | 'edit'
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statsModal, setStatsModal] = useState(null);
  const [sending, setSending] = useState(null); // campaignId being sent
  const [page, setPage] = useState(1);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [perPage, setPerPage] = useState(20);

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const res = await getNotificationCampaigns({ type, page: p, limit: perPage });
      setCampaigns(res.data?.rows || []);
      setTotalCampaigns(res.data?.pagination?.total || 0);
    } catch (e) {
      toast.error('Failed to load campaigns: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [type, page, perPage]);

  useEffect(() => { load(page); }, [load, page]);

  const handleSave = async (payload, intent) => {
    setSaving(true);
    try {
      let campaign;
      if (editTarget) {
        campaign = await updateNotificationCampaign(editTarget.id, payload);
        toast.success('Campaign updated');
      } else {
        const res = await createNotificationCampaign(payload);
        campaign = res.data;
        toast.success(
          intent === 'send_now' ? 'Campaign sent!' :
          intent === 'schedule' ? 'Campaign scheduled' :
          'Draft saved'
        );
      }

      // If send_now intent and we just created → dispatch immediately
      if (intent === 'send_now' && campaign?.id) {
        await sendNotificationCampaign(campaign.id);
      }

      setView('list');
      setEditTarget(null);
      load();
    } catch (e) {
      toast.error('Failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async (campaign) => {
    if (!window.confirm(`Send campaign "${campaign.headline}" now?`)) return;
    setSending(campaign.id);
    try {
      await sendNotificationCampaign(campaign.id);
      toast.success('Campaign dispatched successfully');
      load();
    } catch (e) {
      toast.error('Send failed: ' + e.message);
    } finally {
      setSending(null);
    }
  };

  const handleDelete = async (campaign) => {
    if (!window.confirm(`Delete campaign "${campaign.headline}"? This action cannot be undone.`)) return;
    try {
      await deleteNotificationCampaign(campaign.id);
      toast.success('Campaign deleted');
      load();
    } catch (e) {
      toast.error('Delete failed: ' + e.message);
    }
  };

  if (view === 'create') {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 18 }}>←</button>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Create {type === 'survey' ? 'Survey' : 'Campaign'}</div>
        </div>
        <CampaignForm
          initial={{ type }}
          onSave={handleSave}
          onCancel={() => setView('list')}
          saving={saving}
        />
      </div>
    );
  }

  if (view === 'edit' && editTarget) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button onClick={() => { setView('list'); setEditTarget(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 18 }}>←</button>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Edit Campaign</div>
        </div>
        <CampaignForm
          initial={{
            ...editTarget,
            audience_filter: {
              min_order_count: '',
              last_active_days: '',
              first_order_date_from: '',
              first_order_date_to: '',
              user_ids: [],
              ...(editTarget.audience_filter || {}),
            },
            channel_config: editTarget.channel_config || { push: true, in_app: true },
            survey_config: editTarget.survey_config || EMPTY_CAMPAIGN.survey_config,
            scheduled_at: editTarget.scheduled_at ? new Date(editTarget.scheduled_at).toISOString().slice(0, 16) : '',
            image_url: editTarget.image_url || '',
            deeplink_url: editTarget.deeplink_url || '',
          }}
          onSave={handleSave}
          onCancel={() => { setView('list'); setEditTarget(null); }}
          saving={saving}
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, ...(isMobile ? { flexWrap: 'wrap', gap: 12 } : {}) }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>
          {type === 'promotional' ? 'Create and send promotional broadcasts to segmented audiences.' : 'Create surveys with interactive response options.'}
        </div>
        <Btn variant="primary" onClick={() => setView('create')}>
          + New {type === 'survey' ? 'Survey' : 'Campaign'}
        </Btn>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>⏳ Loading…</div>
      ) : campaigns.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>{type === 'survey' ? '📊' : '📢'}</div>
          No campaigns yet. Create one to get started.
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, ...(isMobile ? { minWidth: 960 } : {}) }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                {['Headline', 'Status', 'Scheduled', 'Targeted', 'Push Sent', 'In-App', 'Created', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {campaigns.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                  <td style={{ padding: '12px 14px', maxWidth: 220 }}>
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.headline}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.body}</div>
                  </td>
                  <td style={{ padding: '12px 14px' }}><Badge variant={statusVariant(c.status)}>{c.status}</Badge></td>
                  <td style={{ padding: '12px 14px', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>{c.scheduled_at ? fmtDate(c.scheduled_at) : 'Immediate'}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600 }}>{c.total_targeted ?? '—'}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>{c.total_push_sent ?? '—'}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600, color: '#7c3aed' }}>{c.total_in_app_created ?? '—'}</td>
                  <td style={{ padding: '12px 14px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(c.created_at)}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: isMobile ? 'nowrap' : 'wrap' }}>
                      {c.status === 'sent' ? (
                        <Btn variant="ghost" style={{ padding: isMobile ? '8px 14px' : '4px 12px', fontSize: isMobile ? 13 : 11, ...(isMobile ? { whiteSpace: 'nowrap' } : {}) }} onClick={() => setStatsModal(c)}>📈 Stats</Btn>
                      ) : (
                        <>
                          {['draft', 'scheduled'].includes(c.status) && (
                            <>
                              <Btn variant="ghost" style={{ padding: isMobile ? '8px 14px' : '4px 12px', fontSize: isMobile ? 13 : 11, ...(isMobile ? { whiteSpace: 'nowrap' } : {}) }}
                                onClick={() => { setEditTarget(c); setView('edit'); }}>✏️ Edit</Btn>
                              <Btn variant="success" style={{ padding: isMobile ? '8px 14px' : '4px 12px', fontSize: isMobile ? 13 : 11, ...(isMobile ? { whiteSpace: 'nowrap' } : {}) }}
                                disabled={sending === c.id}
                                onClick={() => handleSend(c)}>
                                {sending === c.id ? '⏳' : '▶ Send'}
                              </Btn>
                              <Btn variant="danger" style={{ padding: isMobile ? '8px 14px' : '4px 12px', fontSize: isMobile ? 13 : 11, ...(isMobile ? { whiteSpace: 'nowrap' } : {}) }}
                                onClick={() => handleDelete(c)}>🗑 Delete</Btn>
                            </>
                          )}
                          {c.status === 'sending' && (
                            <Badge variant="info">⏳ Sending…</Badge>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && campaigns.length > 0 && (() => {
        const totalPages = Math.ceil(totalCampaigns / perPage);
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Showing {((page - 1) * perPage) + 1}–{Math.min(page * perPage, totalCampaigns)} of {totalCampaigns} campaigns
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>Rows per page:</span>
                <select
                  value={perPage}
                  onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                  style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}
                >
                  {[10, 20, 50, 100].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              {totalPages > 1 && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn variant="ghost" style={{ padding: '5px 12px', fontSize: 12 }}
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}>
                    ← Prev
                  </Btn>
                  <span style={{ fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                    Page {page} / {totalPages}
                  </span>
                  <Btn variant="ghost" style={{ padding: '5px 12px', fontSize: 12 }}
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}>
                    Next →
                  </Btn>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      <StatsModal open={!!statsModal} onClose={() => setStatsModal(null)} campaign={statsModal} />
    </div>
  );
};

// ─── EXPIRY REMINDER TAB ──────────────────────────────────────────────────────

const REWARD_TYPE_LABELS = {
  scratch_card: '🎁 Scratch Card',
  promo_wallet: '💰 Promo Wallet',
};

const ExpiryReminderTab = () => {
  // ── Config section state ──────────────────────────────────────────────────
  const [configs, setConfigs]       = useState([]);
  const [configLoading, setConfigLoading] = useState(true);
  const [editModal, setEditModal]   = useState(null); // config row being edited
  const [form, setForm]             = useState({});
  const [saving, setSaving]         = useState(false);

  // ── History section state ─────────────────────────────────────────────────
  const [logs, setLogs]             = useState([]);
  const [logLoading, setLogLoading] = useState(true);
  const [logTotal, setLogTotal]     = useState(0);
  const [logPage, setLogPage]       = useState(1);
  const LOG_LIMIT                   = 50;

  const [filterType, setFilterType]     = useState('');
  const [filterFrom, setFilterFrom]     = useState('');
  const [filterTo, setFilterTo]         = useState('');

  // ── Load configs ──────────────────────────────────────────────────────────
  const loadConfigs = useCallback(async () => {
    setConfigLoading(true);
    try {
      const res = await getExpiryReminderConfigs();
      setConfigs(res.data || []);
    } catch (e) {
      toast.error('Failed to load configs: ' + e.message);
    } finally {
      setConfigLoading(false);
    }
  }, []);

  // ── Load logs ─────────────────────────────────────────────────────────────
  const loadLogs = useCallback(async (page = 1) => {
    setLogLoading(true);
    try {
      const res = await getExpiryReminderLogs({
        ...(filterType ? { reward_type: filterType } : {}),
        ...(filterFrom ? { date_from: filterFrom } : {}),
        ...(filterTo   ? { date_to:   filterTo   } : {}),
        page,
        limit: LOG_LIMIT,
      });
      setLogs(res.data?.rows || []);
      setLogTotal(res.data?.pagination?.total || 0);
      setLogPage(page);
    } catch (e) {
      toast.error('Failed to load history: ' + e.message);
    } finally {
      setLogLoading(false);
    }
  }, [filterType, filterFrom, filterTo]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);
  useEffect(() => { loadLogs(1);   }, [loadLogs]);

  // ── Edit modal ────────────────────────────────────────────────────────────
  const openEdit = (cfg) => {
    setForm({
      is_enabled:   cfg.is_enabled,
      bucket_day:   cfg.bucket_day,
      title:        cfg.title,
      body:         cfg.body,
      deeplink_url: cfg.deeplink_url || '',
    });
    setEditModal(cfg);
  };

  const handleSave = async () => {
    if (!form.title.trim())          { toast.error('Title is required'); return; }
    if (form.title.length > 100)     { toast.error('Title max 100 chars'); return; }
    if (!form.body.trim())           { toast.error('Body is required'); return; }
    if (form.body.length > 200)      { toast.error('Body max 200 chars'); return; }
    if (!form.bucket_day || parseInt(form.bucket_day) <= 0) {
      toast.error('Bucket day must be a positive number'); return;
    }
    setSaving(true);
    try {
      await updateExpiryReminderConfig(editModal.id, {
        is_enabled:   form.is_enabled,
        bucket_day:   parseInt(form.bucket_day),
        title:        form.title,
        body:         form.body,
        deeplink_url: form.deeplink_url || '',
      });
      toast.success('Config saved');
      setEditModal(null);
      loadConfigs();
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.ceil(logTotal / LOG_LIMIT);

  return (
    <div>
      {/* ── Section: Config ─────────────────────────────────────────────── */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
              ⏰ Expiry Reminder Config
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Controls what the daily 10 AM cron sends for each reward bucket.
              Toggle buckets on/off, edit copy, or change how many days before expiry they fire.
            </div>
          </div>
          <button
            onClick={loadConfigs}
            style={{ fontSize: 12, color: '#6b7280', background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}
          >
            ↻ Refresh
          </button>
        </div>

        {configLoading ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: '#9ca3af' }}>⏳ Loading configs…</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['Reward Type', 'D-N Bucket', 'Status', 'Title', 'Body', 'Deeplink', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {configs.map(cfg => (
                  <tr key={cfg.id} style={{ borderBottom: '1px solid #f3f4f6' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
                      {REWARD_TYPE_LABELS[cfg.reward_type] || cfg.reward_type}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                        fontSize: 12, fontWeight: 700,
                        background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe',
                      }}>
                        D-{cfg.bucket_day}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <Badge variant={cfg.is_enabled ? 'success' : 'default'}>
                        {cfg.is_enabled ? '● Enabled' : '○ Disabled'}
                      </Badge>
                    </td>
                    <td style={{ padding: '12px 14px', maxWidth: 200 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' }}>
                        {cfg.title}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', maxWidth: 260 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280', fontSize: 12 }}>
                        {cfg.body}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', maxWidth: 180 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280', fontSize: 11 }}>
                        {cfg.deeplink_url || '—'}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <Btn variant="ghost" style={{ padding: '5px 14px', fontSize: 12 }} onClick={() => openEdit(cfg)}>
                        ✏️ Edit
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Edit Modal ─────────────────────────────────────────────────────── */}
      <Modal
        open={!!editModal}
        onClose={() => setEditModal(null)}
        title={`Edit — ${REWARD_TYPE_LABELS[editModal?.reward_type] || editModal?.reward_type} D-${editModal?.bucket_day}`}
        width={600}
      >
        {editModal && (
          <div>
            <div style={{ display: 'flex', gap: 24, marginBottom: 18, flexWrap: 'wrap' }}>
              <Toggle
                checked={form.is_enabled}
                onChange={v => setForm(f => ({ ...f, is_enabled: v }))}
                label="Enabled"
              />
            </div>

            <Input
              label="Days before expiry (bucket_day)"
              type="number"
              min={1}
              value={form.bucket_day}
              onChange={e => setForm(f => ({ ...f, bucket_day: e.target.value }))}
            />

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>
                Title{' '}
                <span style={{ color: '#9ca3af', fontWeight: 400 }}>— max 100 chars</span>
              </label>
              <input
                className="form-control"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                maxLength={100}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
              <CharCount value={form.title} max={100} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>
                Body{' '}
                <span style={{ color: '#9ca3af', fontWeight: 400 }}>
                  — max 200 chars
                  {editModal.reward_type === 'promo_wallet' && ' · use {{amount}} for the ₹ value'}
                </span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                maxLength={200}
                style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
              />
              <CharCount value={form.body} max={200} />
            </div>

            <Input
              label="Deeplink URL"
              placeholder="solar.zunhome.in/app/openRoute?pageName=rewardscreen"
              value={form.deeplink_url}
              onChange={e => setForm(f => ({ ...f, deeplink_url: e.target.value }))}
            />

            {editModal.reward_type === 'promo_wallet' && (
              <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                💡 Use <code style={{ background: '#fef3c7', padding: '1px 5px', borderRadius: 3 }}>{'{{amount}}'}</code> in the title or body — it will be replaced with the user's actual wallet balance in ₹ at send time.
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variant="primary" disabled={saving} onClick={handleSave}>
                {saving ? '⏳ Saving…' : '💾 Save Changes'}
              </Btn>
              <Btn variant="ghost" onClick={() => setEditModal(null)}>Cancel</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Section: Send History ────────────────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
              📋 Send History
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Every push notification fired by the expiry-reminder cron. Updates daily after 10 AM IST.
            </div>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#6b7280', marginBottom: 4 }}>Reward Type</label>
              <select
                className="form-control"
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                style={{ fontSize: 12, padding: '5px 10px', minWidth: 160 }}
              >
                <option value="">All types</option>
                <option value="scratch_card">🎁 Scratch Card</option>
                <option value="promo_wallet">💰 Promo Wallet</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#6b7280', marginBottom: 4 }}>From</label>
              <input
                type="date"
                className="form-control"
                value={filterFrom}
                max={filterTo || undefined}
                onChange={e => setFilterFrom(e.target.value)}
                style={{ fontSize: 12, padding: '5px 10px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#6b7280', marginBottom: 4 }}>To</label>
              <input
                type="date"
                className="form-control"
                value={filterTo}
                min={filterFrom || undefined}
                onChange={e => setFilterTo(e.target.value)}
                style={{ fontSize: 12, padding: '5px 10px' }}
              />
            </div>
            <Btn
              variant="primary"
              style={{ padding: '7px 16px', fontSize: 12 }}
              onClick={() => loadLogs(1)}
            >
              🔍 Apply
            </Btn>
            {(filterType || filterFrom || filterTo) && (
              <Btn
                variant="ghost"
                style={{ padding: '7px 12px', fontSize: 12 }}
                onClick={() => { setFilterType(''); setFilterFrom(''); setFilterTo(''); }}
              >
                ✕ Clear
              </Btn>
            )}
          </div>
        </div>

        {logLoading ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: '#9ca3af' }}>⏳ Loading history…</div>
        ) : logs.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9ca3af' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
            No send history yet. Logs appear here after the daily 10 AM cron runs.
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    {['Sent At', 'Reward Type', 'Bucket', 'User ID', 'Amount', 'Title', 'Body'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id} style={{ borderBottom: '1px solid #f3f4f6' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: '#374151', fontSize: 12 }}>
                        {fmtDate(log.sent_at)}
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        <Badge variant={log.reward_type === 'scratch_card' ? 'purple' : 'warning'}>
                          {log.reward_type === 'scratch_card' ? '🎁 Scratch Card' : '💰 Promo Wallet'}
                        </Badge>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                          fontSize: 11, fontWeight: 700,
                          background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe',
                        }}>
                          D-{log.bucket_day}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#374151', fontVariantNumeric: 'tabular-nums' }}>
                        {log.user_id}
                      </td>
                      <td style={{ padding: '10px 14px', color: '#059669', fontWeight: 600 }}>
                        {log.amount != null ? `₹${log.amount}` : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', maxWidth: 220 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#111827' }}>
                          {log.title}
                        </div>
                      </td>
                      <td style={{ padding: '10px 14px', maxWidth: 280 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280', fontSize: 12 }}>
                          {log.body}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Showing {((logPage - 1) * LOG_LIMIT) + 1}–{Math.min(logPage * LOG_LIMIT, logTotal)} of {logTotal} records
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn variant="ghost" style={{ padding: '5px 12px', fontSize: 12 }}
                    disabled={logPage === 1}
                    onClick={() => loadLogs(logPage - 1)}>
                    ← Prev
                  </Btn>
                  <span style={{ fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                    Page {logPage} / {totalPages}
                  </span>
                  <Btn variant="ghost" style={{ padding: '5px 12px', fontSize: 12 }}
                    disabled={logPage >= totalPages}
                    onClick={() => loadLogs(logPage + 1)}>
                    Next →
                  </Btn>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

// ─── FUNNEL RE-ENGAGEMENT TAB ─────────────────────────────────────────────────

const FUNNEL_STAGE_LABELS = {
  view_no_cart:        '👀 View → No Cart',
  cart_no_checkout:    '🛒 Cart → No Checkout',
  checkout_no_payment: '🏁 Checkout → No Payment',
};

const FUNNEL_STAGE_DESC = {
  view_no_cart:        'Viewed a product but never added it to cart.',
  cart_no_checkout:    'Added to cart but never began checkout.',
  checkout_no_payment: 'Placed an order but payment is still pending/failed.',
};

const minutesToLabel = (m) =>
  m == null ? '—' : m >= 1440 ? `${(m / 1440).toFixed(0)}d` : m >= 60 ? `${(m / 60).toFixed(0)}h` : `${m}m`;

const FunnelReengagementTab = () => {
  const isMobile = useIsMobile();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null); // config being edited
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  // ── Send-history state ──────────────────────────────────────────────────
  const [logs, setLogs] = useState([]);
  const [logLoading, setLogLoading] = useState(true);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const LOG_LIMIT = 50;
  const [logStage, setLogStage] = useState('');
  const [logFrom, setLogFrom] = useState('');
  const [logTo, setLogTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFunnelConfigs();
      setConfigs(res.data || []);
    } catch (e) {
      toast.error('Failed to load funnel configs: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async (page = 1) => {
    setLogLoading(true);
    try {
      const res = await getFunnelLogs({
        ...(logStage ? { stage: logStage } : {}),
        ...(logFrom ? { date_from: logFrom } : {}),
        ...(logTo ? { date_to: logTo } : {}),
        page,
        limit: LOG_LIMIT,
      });
      setLogs(res.data?.rows || []);
      setLogTotal(res.data?.pagination?.total || 0);
      setLogPage(page);
    } catch (e) {
      toast.error('Failed to load send history: ' + e.message);
    } finally {
      setLogLoading(false);
    }
  }, [logStage, logFrom, logTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLogs(1); }, [loadLogs]);

  const openEdit = (cfg) => {
    const t = cfg.templates || {};
    setForm({
      is_enabled: cfg.is_enabled,
      nudge1_delay_minutes: cfg.nudge1_delay_minutes,
      nudge2_delay_minutes: cfg.nudge2_delay_minutes,
      hard_stop_minutes: cfg.hard_stop_minutes,
      low_stock_threshold: cfg.low_stock_threshold,
      push: cfg.channel_config?.push !== false,
      in_app: cfg.channel_config?.in_app !== false,
      deeplink_template: cfg.deeplink_template || '',
      n1_headline: t.nudge1?.headline || '',
      n1_body: t.nudge1?.body || '',
      n2_headline: t.nudge2?.headline || '',
      n2_body: t.nudge2?.body || '',
      ls_headline: t.low_stock?.headline || '',
      ls_body: t.low_stock?.body || '',
    });
    setEditModal(cfg);
  };

  const handleSave = async () => {
    // Both nudges require copy; low-stock override is optional but validated if present.
    const checks = [
      ['n1_headline', 'Nudge 1 headline', 50], ['n1_body', 'Nudge 1 body', 120],
      ['n2_headline', 'Nudge 2 headline', 50], ['n2_body', 'Nudge 2 body', 120],
    ];
    for (const [key, label, max] of checks) {
      if (!String(form[key] || '').trim()) { toast.error(`${label} is required`); return; }
      if (form[key].length > max) { toast.error(`${label} max ${max} chars`); return; }
    }
    if ((form.ls_headline || form.ls_body) && (!form.ls_headline.trim() || !form.ls_body.trim())) {
      toast.error('Low-stock override needs both headline and body (or leave both empty)'); return;
    }
    if (parseInt(form.nudge2_delay_minutes) < parseInt(form.nudge1_delay_minutes)) {
      toast.error('Nudge 2 delay must be ≥ nudge 1 delay'); return;
    }
    setSaving(true);
    try {
      await updateFunnelConfig(editModal.stage, {
        is_enabled: form.is_enabled,
        nudge1_delay_minutes: parseInt(form.nudge1_delay_minutes) || 1440,
        nudge2_delay_minutes: parseInt(form.nudge2_delay_minutes) || 2880,
        hard_stop_minutes: parseInt(form.hard_stop_minutes) || 2880,
        low_stock_threshold: parseInt(form.low_stock_threshold) || 10,
        channel_config: { push: form.push, in_app: form.in_app },
        deeplink_template: form.deeplink_template || null,
        templates: {
          nudge1: { headline: form.n1_headline, body: form.n1_body },
          nudge2: { headline: form.n2_headline, body: form.n2_body },
          ...((form.ls_headline && form.ls_body)
            ? { low_stock: { headline: form.ls_headline, body: form.ls_body } }
            : {}),
        },
      });
      toast.success('Funnel config saved');
      setEditModal(null);
      load();
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>⏳ Loading funnel configs…</div>;

  return (
    <div>
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, ...(isMobile ? { minWidth: 860 } : {}) }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              {['Stage', 'Status', 'Nudge 1', 'Nudge 2', 'Hard Stop', 'Low-Stock <', 'Channels', 'Actions'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {configs.map(cfg => (
              <tr key={cfg.stage} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '12px 16px', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
                  {FUNNEL_STAGE_LABELS[cfg.stage] || cfg.stage}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <Badge variant={cfg.is_enabled ? 'success' : 'default'}>
                    {cfg.is_enabled ? '● Enabled' : '○ Disabled'}
                  </Badge>
                </td>
                <td style={{ padding: '12px 16px', color: '#374151' }}>{minutesToLabel(cfg.nudge1_delay_minutes)}</td>
                <td style={{ padding: '12px 16px', color: '#374151' }}>{minutesToLabel(cfg.nudge2_delay_minutes)}</td>
                <td style={{ padding: '12px 16px', color: '#374151' }}>{minutesToLabel(cfg.hard_stop_minutes)}</td>
                <td style={{ padding: '12px 16px', color: '#374151' }}>{cfg.low_stock_threshold}</td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {cfg.channel_config?.push !== false && <Badge variant="info">Push</Badge>}
                    {cfg.channel_config?.in_app !== false && <Badge variant="purple">In-App</Badge>}
                  </div>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <Btn variant="ghost" style={{ padding: '5px 14px', fontSize: 12 }} onClick={() => openEdit(cfg)}>
                    ✏️ Edit
                  </Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      <Modal
        open={!!editModal}
        onClose={() => setEditModal(null)}
        title={`Edit — ${FUNNEL_STAGE_LABELS[editModal?.stage] || editModal?.stage}`}
        width={680}
      >
        {editModal && (
          <div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
              {FUNNEL_STAGE_DESC[editModal.stage]}
            </div>

            <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
              <Toggle checked={form.is_enabled} onChange={v => setForm(f => ({ ...f, is_enabled: v }))} label="Enabled" />
              <div style={{ display: 'flex', gap: 12 }}>
                <Toggle checked={form.push} onChange={v => setForm(f => ({ ...f, push: v }))} label="Push" />
                <Toggle checked={form.in_app} onChange={v => setForm(f => ({ ...f, in_app: v }))} label="In-App" />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Input label="Nudge 1 delay (min)" type="number" min={1}
                  value={form.nudge1_delay_minutes}
                  onChange={e => setForm(f => ({ ...f, nudge1_delay_minutes: e.target.value }))} />
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Input label="Nudge 2 delay (min)" type="number" min={1}
                  value={form.nudge2_delay_minutes}
                  onChange={e => setForm(f => ({ ...f, nudge2_delay_minutes: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Input label="Hard stop (min)" type="number" min={1}
                  value={form.hard_stop_minutes}
                  onChange={e => setForm(f => ({ ...f, hard_stop_minutes: e.target.value }))} />
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Input label="Low-stock threshold" type="number" min={0}
                  value={form.low_stock_threshold}
                  onChange={e => setForm(f => ({ ...f, low_stock_threshold: e.target.value }))} />
              </div>
            </div>

            {/* Nudge 1 */}
            <SectionTitle>Nudge 1 — reminder</SectionTitle>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>Headline <span style={{ color: '#9ca3af' }}>max 50</span></label>
              <input className="form-control" value={form.n1_headline} maxLength={50}
                onChange={e => setForm(f => ({ ...f, n1_headline: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box' }} />
              <CharCount value={form.n1_headline} max={50} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>Body <span style={{ color: '#9ca3af' }}>max 120</span></label>
              <textarea className="form-control" rows={2} value={form.n1_body} maxLength={120}
                onChange={e => setForm(f => ({ ...f, n1_body: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }} />
              <CharCount value={form.n1_body} max={120} />
            </div>

            {/* Nudge 2 */}
            <SectionTitle>Nudge 2 — urgency</SectionTitle>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>Headline <span style={{ color: '#9ca3af' }}>max 50</span></label>
              <input className="form-control" value={form.n2_headline} maxLength={50}
                onChange={e => setForm(f => ({ ...f, n2_headline: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box' }} />
              <CharCount value={form.n2_headline} max={50} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>Body <span style={{ color: '#9ca3af' }}>max 120</span></label>
              <textarea className="form-control" rows={2} value={form.n2_body} maxLength={120}
                onChange={e => setForm(f => ({ ...f, n2_body: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }} />
              <CharCount value={form.n2_body} max={120} />
            </div>

            {/* Low-stock override */}
            <SectionTitle>Low-stock override <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional — used when stock &lt; threshold; supports {'{{units_left}}'})</span></SectionTitle>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>Headline <span style={{ color: '#9ca3af' }}>max 50</span></label>
              <input className="form-control" value={form.ls_headline} maxLength={50}
                onChange={e => setForm(f => ({ ...f, ls_headline: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box' }} />
              <CharCount value={form.ls_headline} max={50} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>Body <span style={{ color: '#9ca3af' }}>max 120</span></label>
              <textarea className="form-control" rows={2} value={form.ls_body} maxLength={120}
                onChange={e => setForm(f => ({ ...f, ls_body: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }} />
              <CharCount value={form.ls_body} max={120} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>
                Deeplink template <span style={{ color: '#9ca3af' }}>— supports {'{{product_id}}'}</span>
              </label>
              <input className="form-control" value={form.deeplink_template}
                onChange={e => setForm(f => ({ ...f, deeplink_template: e.target.value }))}
                style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variant="primary" disabled={saving} onClick={handleSave}>
                {saving ? '⏳ Saving…' : '💾 Save Changes'}
              </Btn>
              <Btn variant="ghost" onClick={() => setEditModal(null)}>Cancel</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Send History ─────────────────────────────────────────────────── */}
      <Card style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
              📋 Send History
            </div>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#6b7280', marginBottom: 4 }}>Stage</label>
              <select
                className="form-control"
                value={logStage}
                onChange={e => setLogStage(e.target.value)}
                style={{ fontSize: 12, padding: '5px 10px', minWidth: 180 }}
              >
                <option value="">All stages</option>
                <option value="view_no_cart">👀 View → No Cart</option>
                <option value="cart_no_checkout">🛒 Cart → No Checkout</option>
                <option value="checkout_no_payment">🏁 Checkout → No Payment</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#6b7280', marginBottom: 4 }}>From</label>
              <input type="date" className="form-control" value={logFrom} max={logTo || undefined}
                onChange={e => setLogFrom(e.target.value)} style={{ fontSize: 12, padding: '5px 10px' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#6b7280', marginBottom: 4 }}>To</label>
              <input type="date" className="form-control" value={logTo} min={logFrom || undefined}
                onChange={e => setLogTo(e.target.value)} style={{ fontSize: 12, padding: '5px 10px' }} />
            </div>
            <Btn variant="primary" style={{ padding: '7px 16px', fontSize: 12 }} onClick={() => loadLogs(1)}>
              🔍 Apply
            </Btn>
            {(logStage || logFrom || logTo) && (
              <Btn variant="ghost" style={{ padding: '7px 12px', fontSize: 12 }}
                onClick={() => { setLogStage(''); setLogFrom(''); setLogTo(''); }}>
                ✕ Clear
              </Btn>
            )}
          </div>
        </div>

        {logLoading ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: '#9ca3af' }}>⏳ Loading send history…</div>
        ) : logs.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9ca3af' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
            No notifications sent yet. Records appear here once the funnel crons send nudges.
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    {['Sent At', 'Stage', 'Nudge', 'User ID', 'Product ID', 'Low Stock', 'Headline', 'Body'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id} style={{ borderBottom: '1px solid #f3f4f6' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: '#374151', fontSize: 12 }}>
                        {fmtDate(log.sent_at)}
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        <Badge variant="info">{FUNNEL_STAGE_LABELS[log.stage] || log.stage}</Badge>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <Badge variant={log.nudge_number === 1 ? 'default' : 'warning'}>#{log.nudge_number}</Badge>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#374151', fontVariantNumeric: 'tabular-nums' }}>{log.user_id}</td>
                      <td style={{ padding: '10px 14px', color: '#374151', fontVariantNumeric: 'tabular-nums' }}>{log.product_id ?? '—'}</td>
                      <td style={{ padding: '10px 14px' }}>
                        {log.low_stock
                          ? <Badge variant="danger">⏳ {log.units_left} left</Badge>
                          : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      <td style={{ padding: '10px 14px', maxWidth: 200 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#111827' }}>{log.headline}</div>
                      </td>
                      <td style={{ padding: '10px 14px', maxWidth: 280 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280', fontSize: 12 }}>{log.body}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {Math.ceil(logTotal / LOG_LIMIT) > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Showing {((logPage - 1) * LOG_LIMIT) + 1}–{Math.min(logPage * LOG_LIMIT, logTotal)} of {logTotal} records
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn variant="ghost" style={{ padding: '5px 12px', fontSize: 12 }}
                    disabled={logPage === 1} onClick={() => loadLogs(logPage - 1)}>← Prev</Btn>
                  <span style={{ fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                    Page {logPage} / {Math.ceil(logTotal / LOG_LIMIT)}
                  </span>
                  <Btn variant="ghost" style={{ padding: '5px 12px', fontSize: 12 }}
                    disabled={logPage >= Math.ceil(logTotal / LOG_LIMIT)} onClick={() => loadLogs(logPage + 1)}>Next →</Btn>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

const TABS = [
  // { key: 'transactional',     label: '⚡ Transactional',      desc: 'Auto-triggered' },
  { key: 'promotional',       label: '📢 Promotional',         desc: 'Manual broadcasts' },
  { key: 'survey',            label: '📊 Surveys',             desc: 'Interactive feedback' },
  { key: 'expiry_reminders',  label: '⏰ Expiry Reminders',    desc: 'Config & history' },
  { key: 'funnel',            label: '🛒 Funnel Recovery',     desc: 'Abandoned-funnel nudges' },
];

const Notifications = () => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('promotional');

  // Access guard — same pattern as other superAdmin pages
  const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
  if (userData.role !== 'superAdmin' && userData.role !== 'admin' && !userData.access?.includes('marketing')) {
    window.location.href = '/orders';
    return null;
  }

  return (
    <div style={{ padding: isMobile ? '16px' : '24px 28px', minHeight: '100vh', background: '#f8fafc' }}>
      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>🔔 Notifications</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
          Manage transactional triggers, create campaigns and surveys, track delivery and engagement.
        </div>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: 24,
        background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)', width: 'fit-content',
        ...(isMobile ? { maxWidth: '100%', overflowX: 'auto', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch' } : {}),
      }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: isMobile ? '10px 16px' : '12px 24px', border: 'none', cursor: 'pointer',
            borderRight: '1px solid #e5e7eb',
            ...(isMobile ? { flex: '0 0 auto', whiteSpace: 'nowrap' } : {}),
            background: activeTab === tab.key ? '#1e40af' : 'white',
            color: activeTab === tab.key ? 'white' : '#374151',
            transition: 'all 0.15s',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{tab.label}</div>
            <div style={{ fontSize: 10, opacity: 0.7, marginTop: 1 }}>{tab.desc}</div>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'transactional'    && <TriggerConfigTab />}
        {activeTab === 'promotional'      && <CampaignsTab type="promotional" />}
        {activeTab === 'survey'           && <CampaignsTab type="survey" />}
        {activeTab === 'expiry_reminders' && <ExpiryReminderTab />}
        {activeTab === 'funnel'           && <FunnelReengagementTab />}
      </div>
    </div>
  );
};

export default Notifications;
